import { useState, useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { resolveImageUrl } from '@/utils/imageUrl';

const API = process.env.REACT_APP_BACKEND_URL;

export const ImageUpload = ({ value, onChange, label, className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5 MB)');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const token = localStorage.getItem('hke_admin_token');
      const res = await axios.post(`${API}/api/admin/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      onChange(res.data.url);
      toast.success('Bild hochgeladen');
    } catch {
      toast.error('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const currentUrl = resolveImageUrl(value);

  return (
    <div className={className}>
      {label && <label className="text-sm text-slate-600 block mb-1.5 font-medium">{label}</label>}
      <div
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !uploading && fileRef.current?.click()}
        className={`border-2 border-dashed p-4 text-center cursor-pointer transition-all duration-200 ${
          dragOver ? 'border-[#0077D4] bg-blue-50' : 'border-slate-300 hover:border-[#0077D4]'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        data-testid="image-upload-area"
      >
        {uploading ? (
          <div className="py-6">
            <Loader2 size={28} className="mx-auto text-[#0077D4] animate-spin mb-2" />
            <p className="text-sm text-slate-500">Wird hochgeladen...</p>
          </div>
        ) : currentUrl ? (
          <div className="relative inline-block">
            <img src={currentUrl} alt="" className="max-h-32 mx-auto object-contain" />
            <button
              onClick={e => { e.stopPropagation(); onChange(''); }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-[#CC2229] text-white flex items-center justify-center hover:bg-red-700 transition-colors"
              data-testid="image-upload-remove"
            >
              <X size={14} />
            </button>
            <p className="text-xs text-slate-400 mt-2">Klicken zum Ändern</p>
          </div>
        ) : (
          <div className="py-6">
            <Upload size={28} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600">Bild hierher ziehen oder klicken</p>
            <p className="text-xs text-slate-400 mt-1">Max. 5 MB · JPG, PNG, GIF, WebP</p>
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={e => handleUpload(e.target.files[0])}
        className="hidden"
        data-testid="image-upload-input"
      />
    </div>
  );
};
